/**
 * TIDRAPPORTEN — veckan, dagarna och vägen in till en ny rad.
 *
 * BÅDE MÅNADEN OCH VECKAN, precis som prototypens skärmkarta begär: noden "tid"
 * har BÅDE "Stor månadskalender" och "Lås veckan" under sig, så månadsrutnätet
 * hör hemma här. Det ligger överst som NAVIGERING — ett sätt att bläddra sig till
 * en dag — och veckoremsan under det är den som bär ett TILLSTÅND.
 *
 * VECKAN ÄR ÄNDÅ ENDA PERIODBEGREPPET (beslut 04): attest per vecka, lås per
 * vecka, ISO 'YYYY-Www'. Månadsrutnätet får därför ingen månadstotal och ingen
 * låsning per månad — det finns ingen sådan operation i kontraktet, där det
 * heter 'week:lock', en vecka i taget, och låsningen hör dessutom hemma på
 * attestskärmen (MANAGER_ONLY).
 *
 * ALDRIG ETT BELOPP HÄR. Skärmen visar TIMMAR och ingenting annat. Tidraderna
 * kommer in som TimeEntryWorkerRow, som saknar prisfälten helt (beslut 16), så
 * ett belopp går inte ens att räkna fram — vilket är precis meningen.
 *
 * BESLUT 10 SYNS I VECKANS TILLSTÅND: en chef som sparar sin egen tid får veckan
 * satt till 'approved' direkt av servern, och får ändå fortsätta skriva i den
 * (MANAGER_OWN_WEEK_WRITABLE_STATES). "Lämna in" visas därför bara när
 * WEEK_TRANSITIONS.submit faktiskt tillåter övergången från det läge veckan står
 * i — knappen är härledd ur tabellen, inte ur en gissning.
 */

import { useMemo, useState } from 'react';
import type { AbsenceKind, SyncOp, TimeEntryWorkerRow, WorkDate } from '@delat/types';
import {
  Bricka,
  Datumrad,
  FAKTOR,
  Felruta,
  Installningsrad,
  Kalenderremsa,
  Knapp,
  Laddar,
  Listrad,
  Manadsrutnat,
  Snabbrad,
  Tomtext,
  Valfalt,
  Veckonavigering,
  Yta,
  franHeltal,
} from '../ui';
import type { Dagsuppgift, Manadsdag } from '../ui';
import {
  arHelg,
  dagarIVecka,
  idag,
  plusDagar,
  veckaFor,
  visaDag,
  visaMinuter,
} from '../app/datum';
import { useNavigering } from '../app/navigering';
import { useInloggad } from '../app/session';
import { FRANVAROSLAG } from '../api/granskare/tidrapport';
import { manadFor, manadensDagar, visaManad } from './oversikt';
import { useSynk } from './tidrapport/lager';
import type { Ogonblicksbild, Synk } from './tidrapport/lager';
import { felAvOperation, useSparning } from './tidrapport/spara';
import {
  franvaroOp,
  nyttFranvaroId,
  nyttTidradId,
  raderaFranvaroOp,
  raderaTidradOp,
  tidradOp,
  veckoOp,
} from './tidrapport/skriv';
import {
  FRANVAROKORT,
  FRANVAROTEXT,
  VECKOLAGETEXT,
  VECKOLAGETON,
  dagenston,
  farRapportera,
  farSkrivaVecka,
  franvaroPaDag,
  kanLamnaIn,
  minuterPaDag,
  momentMedId,
  orderMedId,
  ordernamn,
  veckansLage,
} from './tidrapport/regler';

/** Hur långt bakåt "Kopiera senaste dagen" letar efter något att kopiera. */
const KOPIERINGSFONSTER_DAGAR = 7;

export const Tid = () => {
  const { firmaId } = useInloggad();
  const synk = useSynk(firmaId);

  if (synk.bild === null) {
    return synk.fel === null ? (
      <Laddar rader={5} />
    ) : (
      <Felruta
        fel={synk.fel}
        atgard={
          <Knapp variant="piller-mini" onClick={() => void synk.synka(true)}>
            Försök igen
          </Knapp>
        }
      />
    );
  }
  return <Tidrapporten bild={synk.bild} synk={synk} />;
};

const Tidrapporten = ({
  bild,
  synk,
}: {
  readonly bild: Ogonblicksbild;
  readonly synk: Synk;
}) => {
  const { konto, roll, arChef, arLast } = useInloggad();
  const { ga } = useNavigering();
  const sparning = useSparning(synk.skicka);

  const [vald, sattVald] = useState<WorkDate>(() => idag());
  const [markerad, sattMarkerad] = useState<string | null>(null);
  const [franvaroOppen, sattFranvaroOppen] = useState(false);
  const [franvaroslag, sattFranvaroslag] = useState<AbsenceKind>('vacation');

  const vecka = veckaFor(vald);
  const lage = veckansLage(bild, konto.id, vecka);
  const rapporterar = farRapportera(roll);
  const farSkriva =
    rapporterar && !arLast && farSkrivaVecka({ lage, egen: true, arChef });

  // BARA MINA EGNA RADER. "Mitt jobb" är personens egen tidrapport; chefen ser
  // de andras under Laget, inte här.
  const minaRader = useMemo(
    () => bild.tidrader.filter((rad) => rad.account_id === konto.id),
    [bild.tidrader, konto.id],
  );
  const minFranvaro = useMemo(
    () => bild.franvaro.filter((rad) => rad.account_id === konto.id),
    [bild.franvaro, konto.id],
  );

  const veckansDagar = dagarIVecka(vecka);
  const veckansMinuter = useMemo(
    () =>
      veckansDagar.reduce((summa, dag) => summa + Number(minuterPaDag(minaRader, dag)), 0),
    [veckansDagar, minaRader],
  );

  const dagar = useMemo<Readonly<Partial<Record<string, Dagsuppgift>>>>(() => {
    const nu = idag();
    const ut: Record<string, Dagsuppgift> = {};
    for (const dag of veckansDagar) {
      const minuter = minuterPaDag(minaRader, dag);
      const franvarande = franvaroPaDag(minFranvaro, dag);
      ut[dag] = {
        ton: dagenston({
          minuter,
          franvarande: franvarande !== null,
          helg: arHelg(dag),
          framtid: dag > nu,
        }),
        text:
          Number(minuter) > 0
            ? franHeltal(Number(minuter), FAKTOR.timmar_minuter)
            : franvarande !== null
              ? FRANVAROKORT[franvarande.kind]
              : undefined,
      };
    }
    return ut;
  }, [veckansDagar, minaRader, minFranvaro]);

  /**
   * MÅNADSRUTNÄTET, byggt ur SAMMA rader som veckoremsan ovan och med SAMMA
   * tonregel (dagenston). Ingen extra hämtning: skärmen har redan hela
   * ögonblicksbilden, och två datakällor på en skärm hade förr eller senare
   * börjat säga olika saker om samma dag.
   */
  const manad = useMemo(() => manadFor(vald), [vald]);
  const rutnat = useMemo<{ readonly dagar: readonly Manadsdag[]; readonly tomma: number }>(() => {
    const nu = idag();
    const { dagar: alla, tomma } = manadensDagar(manad);
    return {
      tomma,
      dagar: alla.map((dag) => {
        const minuter = minuterPaDag(minaRader, dag);
        const franvarande = franvaroPaDag(minFranvaro, dag);
        return {
          dag,
          ton: dagenston({
            minuter,
            franvarande: franvarande !== null,
            helg: arHelg(dag),
            framtid: dag > nu,
          }),
          etikett:
            Number(minuter) > 0
              ? visaMinuter(minuter)
              : franvarande !== null
                ? FRANVAROKORT[franvarande.kind]
                : undefined,
          idag: dag === nu,
          helg: arHelg(dag),
        };
      }),
    };
  }, [manad, minaRader, minFranvaro]);

  const dagensRader = useMemo(
    () =>
      minaRader
        .filter((rad) => rad.work_date === vald)
        // Id:t är en ULID och alltså sorterbart i skapandeordning.
        .sort((a, b) => a.id.localeCompare(b.id)),
    [minaRader, vald],
  );
  const dagensMinuter = minuterPaDag(minaRader, vald);
  const dagensFranvaro = franvaroPaDag(minFranvaro, vald);

  /** Senaste dagen FÖRE den valda som har något att kopiera. */
  const kalldag = useMemo<WorkDate | null>(() => {
    for (let n = 1; n <= KOPIERINGSFONSTER_DAGAR; n += 1) {
      const dag = plusDagar(vald, -n);
      if (minaRader.some((rad) => rad.work_date === dag)) return dag;
    }
    return null;
  }, [vald, minaRader]);

  const kor = (bygg: () => readonly SyncOp[]): void => {
    sparning.nollstall();
    void sparning.spara(bygg);
  };

  const kopiera = (): void => {
    if (kalldag === null) return;
    kor(() =>
      minaRader
        .filter((rad) => rad.work_date === kalldag)
        .map((rad) =>
          tidradOp({
            id: nyttTidradId(),
            account_id: konto.id,
            work_date: vald,
            minutes: rad.minutes,
            task_id: rad.task_id,
            work_order_id: rad.work_order_id,
            note: rad.note,
            // Kopian är skriven för hand även när originalet kom ur rösten.
            source: 'manual',
            base_version: null,
          }),
        ),
    );
  };

  const taBort = (rad: TimeEntryWorkerRow): void => {
    sattMarkerad(null);
    kor(() => [raderaTidradOp(rad.id, rad.version)]);
  };

  const sparaFranvaro = (): void => {
    kor(() => [
      franvaroOp({
        id: nyttFranvaroId(),
        account_id: konto.id,
        work_date: vald,
        kind: franvaroslag,
        base_version: null,
      }),
    ]);
    sattFranvaroOppen(false);
  };

  const markeradRad = dagensRader.find((rad) => rad.id === markerad) ?? null;

  return (
    <>
      {/* Bara månaden. "navigering — veckan är perioden" stod här förut och
          sa samma sak som hjälptexten längst ner. */}
      <Datumrad stark={visaManad(manad)} />

      <Veckonavigering
        vecka={vecka}
        summa={visaMinuter(veckansMinuter)}
        onForegaende={() => {
          sattVald(plusDagar(vald, -7));
          sattMarkerad(null);
        }}
        onNasta={() => {
          sattVald(plusDagar(vald, 7));
          sattMarkerad(null);
        }}
      />

      {/*
        MÅNADEN ÖVERST, VECKAN UNDER. Rutnätet ligger DIREKT på skärmen och inte
        i en <Yta>: .storkal bär redan egen skugga, egen hårfin ram och egen
        radie, så ett kort runt det hade blivit "ram inuti ramen" (appbokens
        "Inte så här"-panel). Prototypen ställer det på samma sätt.
      */}
      <Manadsrutnat
        dagar={rutnat.dagar}
        tomma={rutnat.tomma}
        vald={vald}
        onValj={(dag) => {
          sattVald(dag);
          sattMarkerad(null);
          sattFranvaroOppen(false);
        }}
      />

      <Kalenderremsa
        vecka={vecka}
        dagar={dagar}
        vald={vald}
        onValj={(dag) => {
          sattVald(dag);
          sattMarkerad(null);
          sattFranvaroOppen(false);
        }}
      />

      <Installningsrad
        ikon="attest"
        etikett="Veckan är"
        varde={<Bricka ton={VECKOLAGETON[lage]}>{VECKOLAGETEXT[lage]}</Bricka>}
        atgard={
          rapporterar && !arLast && kanLamnaIn(lage) ? (
            /*
              'submitted' STÅR MED BLAND submit-övergångens från-tillstånd
              (WEEK_TRANSITIONS): den som rättat en rad i en redan inlämnad vecka
              MÅSTE kunna lämna in igen. Knappen får därför inte tas bort — men
              den ska inte heller se ut som om ingenting hänt. Namnet och
              förklaringen byts i stället, så att ett andra tryck är ett medvetet
              val och inte en gissning på något som redan är gjort.
            */
            <Knapp
              variant="piller-mini"
              disabled={sparning.sparar}
              onClick={() => kor(() => [veckoOp(konto.id, vecka, 'submit')])}
              title={
                lage === 'submitted'
                  ? 'Veckan är redan inlämnad. Tryck bara om du rättat något efteråt.'
                  : undefined
              }
            >
              {lage === 'submitted' ? 'Lämna in igen' : 'Lämna in'}
            </Knapp>
          ) : undefined
        }
      />

      <Yta rubrik={visaDag(vald)} rakning={visaMinuter(dagensMinuter)}>
        {dagensFranvaro !== null && (
          <Listrad titel={FRANVAROTEXT[dagensFranvaro.kind]} under="Frånvaro — ingen tid i dag" />
        )}
        {dagensRader.map((rad) => (
          <Listrad
            key={rad.id}
            titel={ordernamn(orderMedId(bild, rad.work_order_id))}
            bi={momentMedId(bild, rad.task_id)}
            under={rad.note ?? undefined}
            varde={visaMinuter(rad.minutes)}
            onClick={
              farSkriva
                ? () => sattMarkerad(markerad === rad.id ? null : rad.id)
                : undefined
            }
          />
        ))}
        {dagensRader.length === 0 && dagensFranvaro === null && (
          <Tomtext>Inget rapporterat den här dagen än.</Tomtext>
        )}
      </Yta>

      {markeradRad !== null && (
        <Snabbrad>
          <Knapp variant="snabb" onClick={() => sattMarkerad(null)}>
            Behåll
          </Knapp>
          <Knapp
            variant="snabb"
            disabled={sparning.sparar}
            onClick={() => taBort(markeradRad)}
          >
            Ta bort {visaMinuter(markeradRad.minutes)}
          </Knapp>
        </Snabbrad>
      )}

      {farSkriva && (
        <Snabbrad>
          <Knapp variant="snabb" disabled={kalldag === null || sparning.sparar} onClick={kopiera}>
            {kalldag !== null && kalldag === plusDagar(vald, -1)
              ? 'Kopiera igår'
              : 'Kopiera senaste dagen'}
          </Knapp>
          <Knapp variant="snabb" onClick={() => sattFranvaroOppen(!franvaroOppen)}>
            Frånvaro
          </Knapp>
        </Snabbrad>
      )}

      {franvaroOppen && farSkriva && (
        <Yta rubrik="Frånvaro" rakning={visaDag(vald)}>
          {dagensFranvaro === null ? (
            <>
              <Valfalt
                etikett="Vad gäller det?"
                varde={franvaroslag}
                onAndra={sattFranvaroslag}
                val={FRANVAROSLAG.map((slag) => ({
                  varde: slag,
                  etikett: FRANVAROTEXT[slag],
                }))}
                hjalp="En frånvaro per dag. Annat om dagen skriver du i anteckningen på en tidrad."
              />
              <Knapp variant="stor-tyst" disabled={sparning.sparar} onClick={sparaFranvaro}>
                Spara frånvaro
              </Knapp>
            </>
          ) : (
            <Knapp
              variant="stor-tyst"
              disabled={sparning.sparar}
              onClick={() => {
                kor(() => [raderaFranvaroOp(dagensFranvaro.id, dagensFranvaro.version)]);
                sattFranvaroOppen(false);
              }}
            >
              Ta bort {FRANVAROTEXT[dagensFranvaro.kind].toLowerCase()}
            </Knapp>
          )}
        </Yta>
      )}

      {farSkriva && (
        <Knapp variant="stor" ikon="tid" onClick={() => ga('tidny')}>
          Lägg till tid
        </Knapp>
      )}

      {sparning.fel !== null && (
        <Felruta
          fel={sparning.fel}
          atgard={
            <Knapp
              variant="piller-mini"
              disabled={sparning.sparar}
              onClick={() => void sparning.spara(() => [])}
            >
              Försök igen
            </Knapp>
          }
        />
      )}
      {sparning.fallna.map((rad) => (
        <Felruta key={rad.op_id} fel={felAvOperation(rad)} />
      ))}

      {!rapporterar && (
        <Tomtext>
          Du läser firmans tid men rapporterar inte själv, så den här skärmen är tom
          för dig. Lagets siffror står under Laget.
        </Tomtext>
      )}
      {arLast && rapporterar && (
        <Tomtext>
          Prenumerationen är pausad. Allt går att läsa och exportera, men inget nytt
          kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}
      {!farSkriva && rapporterar && !arLast && (
        <Tomtext>
          Veckan är {VECKOLAGETEXT[lage].toLowerCase()} och kan inte ändras. Be chefen
          öppna den igen om något ska rättas.
        </Tomtext>
      )}
      <Tomtext>
        Tryck på en dag. Ledighet läggs in under Frånvaro. Attest och lås går per
        vecka, aldrig per månad.
      </Tomtext>
    </>
  );
};
